import React, { useState, useEffect } from 'react';
import { notificationAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SafeIcon from '../../common/SafeIcon';
import { FiSave, FiSettings, FiClock, FiAlertTriangle, FiGift, FiFileText, FiUser } from 'react-icons/fi';
import toast from 'react-hot-toast';

const AdminNotificationSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await notificationAPI.getSettings();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('ไม่สามารถโหลดการตั้งค่าได้');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await notificationAPI.updateSettings(settings);
      toast.success('บันทึกการตั้งค่าเรียบร้อยแล้ว');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('ไม่สามารถบันทึกการตั้งค่าได้');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!settings) return <div className="p-8 text-center text-red-500">Error loading settings</div>;

  return (
    <div className="px-4 sm:px-6 lg:px-8 pb-20 lg:pb-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">ตั้งค่าการแจ้งเตือน</h1>
        <p className="text-gray-600">จัดการการแจ้งเตือนอัตโนมัติของระบบ</p>
      </div>

      <div className="max-w-3xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Schedule Settings */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <SafeIcon icon={FiClock} className="mr-2 text-indigo-500" />
              กำหนดเวลา (Schedule)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
               <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">เวลาตรวจสอบรายวัน</label>
                  <p className="text-xs text-gray-500 mb-2">ระบบจะตรวจสอบและส่งสรุปแจ้งเตือนในเวลานี้ทุกวัน</p>
                  <input
                    type="time"
                    name="cronTime"
                    value={settings.cronTime}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
               </div>
            </div>
          </div>

          {/* Notification Types */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <SafeIcon icon={FiSettings} className="mr-2 text-indigo-500" />
              ประเภทการแจ้งเตือน
            </h3>
            <div className="space-y-6">
              
              {/* Birthday */}
              <div>
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="enableBirthday"
                      name="enableBirthday"
                      type="checkbox"
                      checked={settings.enableBirthday}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="enableBirthday" className="font-medium text-gray-700 flex items-center">
                      <SafeIcon icon={FiGift} className="mr-2 text-pink-500" /> วันเกิด (Birthdays)
                    </label>
                    <p className="text-gray-500">แจ้งเตือนวันเกิดของลูกทีมให้หัวหน้าทราบ</p>
                  </div>
                </div>
                {settings.enableBirthday && (
                   <div className="ml-7 mt-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">แจ้งเตือนล่วงหน้า (วัน)</label>
                      <input
                        type="number"
                        name="birthdayReminderDays"
                        value={settings.birthdayReminderDays}
                        onChange={handleChange}
                        min="0"
                        className="w-24 border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                   </div>
                )}
              </div>

              {/* License Expiry */}
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-start mb-3">
                  <div className="flex items-center h-5">
                    <input
                      id="enableLicenseExpiry"
                      name="enableLicenseExpiry"
                      type="checkbox"
                      checked={settings.enableLicenseExpiry}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="enableLicenseExpiry" className="font-medium text-gray-700 flex items-center">
                      <SafeIcon icon={FiUser} className="mr-2 text-orange-500" /> ใบอนุญาตหมดอายุ (License Expiry)
                    </label>
                    <p className="text-gray-500">แจ้งเตือนก่อนใบอนุญาตตัวแทนหมดอายุ</p>
                  </div>
                </div>
                {settings.enableLicenseExpiry && (
                   <div className="ml-7">
                      <label className="block text-sm font-medium text-gray-700 mb-1">แจ้งเตือนล่วงหน้า (วัน)</label>
                      <input
                        type="number"
                        name="licenseExpiryDays"
                        value={settings.licenseExpiryDays}
                        onChange={handleChange}
                        min="1"
                        className="w-24 border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                   </div>
                )}
              </div>

              {/* Policy Expiry */}
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-start mb-3">
                  <div className="flex items-center h-5">
                    <input
                      id="enablePolicyExpiry"
                      name="enablePolicyExpiry"
                      type="checkbox"
                      checked={settings.enablePolicyExpiry}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="enablePolicyExpiry" className="font-medium text-gray-700 flex items-center">
                      <SafeIcon icon={FiFileText} className="mr-2 text-blue-500" /> กรมธรรม์ครบกำหนด (Policy Renewal)
                    </label>
                    <p className="text-gray-500">แจ้งเตือนเมื่อกรมธรรม์ใกล้ครบกำหนดชำระเบี้ย</p>
                  </div>
                </div>
                {settings.enablePolicyExpiry && (
                   <div className="ml-7">
                      <label className="block text-sm font-medium text-gray-700 mb-1">แจ้งเตือนล่วงหน้า (วัน)</label>
                      <input
                        type="number"
                        name="policyExpiryDays"
                        value={settings.policyExpiryDays}
                        onChange={handleChange}
                        min="1"
                        className="w-24 border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                   </div>
                )}
              </div>

              {/* Calendar Events */}
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="enableCalendar"
                      name="enableCalendar"
                      type="checkbox"
                      checked={settings.enableCalendar}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="enableCalendar" className="font-medium text-gray-700 flex items-center">
                      <SafeIcon icon={FiClock} className="mr-2 text-purple-500" /> กิจกรรมในปฏิทิน (Calendar Events)
                    </label>
                    <p className="text-gray-500">แจ้งเตือนกิจกรรมที่เกิดขึ้นในวันนี้</p>
                  </div>
                </div>
              </div>

              {/* Training */}
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="enableTraining"
                      name="enableTraining"
                      type="checkbox"
                      checked={settings.enableTraining}
                      onChange={handleChange}
                      className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="enableTraining" className="font-medium text-gray-700 flex items-center">
                      <SafeIcon icon={FiAlertTriangle} className="mr-2 text-green-500" /> การอบรม (Training)
                    </label>
                    <p className="text-gray-500">แจ้งเตือนคอร์สอบรมที่มีในวันนี้</p>
                  </div>
                </div>
                {settings.enableTraining && (
                   <div className="ml-7 mt-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">แจ้งเตือนล่วงหน้า (วัน)</label>
                      <input
                        type="number"
                        name="trainingReminderDays"
                        value={settings.trainingReminderDays}
                        onChange={handleChange}
                        min="0"
                        className="w-24 border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                   </div>
                )}
              </div>

            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              <SafeIcon icon={FiSave} className="mr-2 -ml-1 h-5 w-5" />
              {saving ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่า'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminNotificationSettings;
